import React, { useEffect, useState } from "react";
import Popper from "@mui/material/Popper";
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Grow,
  IconButton,
  Link,
  MenuItem,
  MenuList,
  Modal,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import Table from "components/Table";
import ClientGroupModal from "components/Modals/ClientGroup";
import PrivateClientGroupIcon from "components/PrivateClientGroupIcon";
import { ProtectedPaths } from "routes";
import { getClientGroups, deleteClientGroup } from "api/resources/clientGroups";
import { ClientGroup, ClientGroupMember } from "api/models/ClientGroup";

const ClientGroupsTable = () => {
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState<ClientGroup | null>(null);

  useEffect(() => {
    fetchClientGroups();
  }, []);

  const fetchClientGroups = () => {
    setLoading(true);
    getClientGroups()
      .then((response) => {
        setClientGroups(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching client groups:", error);
        setLoading(false);
      });
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }: { row: { original: ClientGroup } }) => (
        <>
          {row.original.name}
          {!row.original.visibleToClients && <PrivateClientGroupIcon />}
        </>
      ),
    },
    {
      accessorKey: "clients",
      header: "Clients",
      cell: ({ row }: { row: { original: ClientGroup } }) => {
        const linkedClients = row.original.clients;
        return (
          <>
            {linkedClients.slice(0, 2).map((client: ClientGroupMember) => (
              <Link
                href={`/${ProtectedPaths.Clients}/${client.id}`}
                key={`ClientChip-${client.id}`}
              >
                <Chip
                  label={client.name}
                  size="small"
                  sx={{ mr: 1 }}
                  clickable
                />
              </Link>
            ))}
            {linkedClients.length > 2 && (
              <Chip
                label={`+ ${linkedClients.length - 2}`}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "settings",
      header: "",
      cell: (row: { row: { original: ClientGroup } }) => {
        const [open, setOpen] = React.useState(false);
        const [anchorElDelete, setAnchorElDelete] =
          React.useState<HTMLElement | null>(null);
        const anchorRef = React.useRef<HTMLButtonElement>(null);

        const handleClickDelete = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorElDelete(event.currentTarget);
        };

        const handleCloseDelete = () => setAnchorElDelete(null);

        const openDelete = Boolean(anchorElDelete);

        const handleToggle = () => {
          setOpen((prevOpen) => !prevOpen);
        };

        const handleClose = (event: Event | React.SyntheticEvent) => {
          if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
          ) {
            return;
          }

          setOpen(false);
        };

        function handleListKeyDown(event: React.KeyboardEvent) {
          if (event.key === "Tab") {
            event.preventDefault();
            setOpen(false);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }

        const prevOpen = React.useRef(open);
        React.useEffect(() => {
          if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
          }

          prevOpen.current = open;
        }, [open]);

        const handleDeleteClientGroup = (id: number) => {
          deleteClientGroup(id)
            .then(() => {
              fetchClientGroups();
              handleCloseDelete();
            })
            .catch((error) => {
              console.error("Error deleting client group:", error);
            });
        };

        return (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              ref={anchorRef}
              id="composition-button"
              aria-controls={open ? "composition-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              placement="right-start"
              transition
              disablePortal
            >
              {({ TransitionProps }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: "right top",
                  }}
                >
                  <Paper style={{ marginRight: "1rem" }}>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        autoFocusItem={open}
                        id="composition-menu"
                        aria-labelledby="composition-button"
                        onKeyDown={handleListKeyDown}
                      >
                        <MenuItem
                          onClick={() => {
                            setEditModal(row.row.original);
                            setOpen(false);
                          }}
                        >
                          Edit
                        </MenuItem>
                        <MenuItem onClick={handleClickDelete}>Delete</MenuItem>
                        <Popover
                          open={openDelete}
                          anchorEl={anchorElDelete}
                          onClose={handleCloseDelete}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          slotProps={{
                            paper: {
                              sx: { mt: 2 },
                            },
                          }}
                        >
                          <div style={{ padding: "1rem" }}>
                            <Typography>Are you sure?</Typography>
                            <Stack direction="row" spacing={1} mt={1}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  handleDeleteClientGroup(row.row.original.id)
                                }
                              >
                                Yes, delete
                              </Button>
                              <Button size="small" onClick={handleCloseDelete}>
                                Cancel
                              </Button>
                            </Stack>
                          </div>
                        </Popover>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Client groups</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          Add group
        </Button>
      </Box>
      <Table
        data={clientGroups}
        columns={columns}
        isFetching={loading}
        pageCount={1}
      />
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <ClientGroupModal
          allGroups={clientGroups}
          onSaved={fetchClientGroups}
          onClose={() => setOpenModal(false)}
        />
      </Modal>
      <Modal open={!!editModal} onClose={() => setEditModal(null)}>
        <ClientGroupModal
          group={editModal ?? undefined}
          allGroups={clientGroups}
          onSaved={fetchClientGroups}
          onClose={() => setEditModal(null)}
        />
      </Modal>
    </>
  );
};

export default ClientGroupsTable;
